// Oturum çözülünce dili yeniden değerlendir.
//
// Token `locale` claim'i mevcuttu ve HİÇBİR yüzey okumuyordu: jwt-verifier
// claim'i taşıyor, scope-resolver çözüyor, GET /v1/me alan olarak döndürüyor
// — ve her tüketici sadece employee_id/roles alıp gerisini atıyordu. Sonuç:
// Keycloak giriş ekranında Türkçe seçen kullanıcı panelde İngilizce görüyordu.
//
// Boot anında claim yok (oturum henüz çözülmedi), bu yüzden dil iki aşamada
// belirlenir: initI18n saklanan seçim + tarayıcıyla ilk boyamayı yapar, bu
// hook /v1/me gelince zinciri claim ile yeniden koşturur.
//
// KULLANICI SEÇİMİ EZİLMEZ: saklanmış bir seçim varsa hook hiç karışmaz.
// Aksi halde EN'e geçen kullanıcının dili bir sonraki /v1/me yanıtında
// claim'e geri döner ve seçici bozuk görünürdü.

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import { cacheResolvedLocale, readStoredLocale, resolveAppLocale } from "@novemai/i18n-sdk";

import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";
import { platformGraph } from "@/api/platform-graph";

import { CUSTOMER_APP_DEFAULT_LOCALE, i18n } from "./index.ts";
import { localeStorage } from "./locale-storage.ts";

/** /v1/me'nin dil için okunan dilimi. */
interface MeLocale {
  readonly locale: string | null;
}

type MeGet = (path: "/v1/me") => Promise<EnvelopedFetchResult<MeLocale>>;

async function fetchMeLocale(): Promise<MeLocale> {
  const get = platformGraph.GET as unknown as MeGet;
  const data = await unwrapEnvelope(get("/v1/me"));
  return { locale: data.locale };
}

/**
 * Oturum açıkken /v1/me'den gelen locale claim'ini i18next'e uygular.
 *
 * Sorgu anahtarı ["me","locale"]: mevcut ["me","identity"] anahtarını paylaşmak
 * cazip ama tehlikeli — aynı anahtara farklı queryFn bağlayan iki hook'ta
 * hangisinin kazandığı mount sırasına bağlıdır ve kaybeden shape'te `locale`
 * yoktur. Bu sessizce "dil yine İngilizce" olarak görünürdü.
 */
export function useApplyResolvedLocale(): void {
  const auth = useAuth();
  const isAuthenticated = auth.isAuthenticated;

  const { data } = useQuery({
    queryKey: ["me", "locale"],
    queryFn: fetchMeLocale,
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });

  const claimLocale = data?.locale ?? undefined;

  useEffect(() => {
    if (readStoredLocale(localeStorage) !== undefined) return;
    if (claimLocale === undefined) return;
    const resolved = resolveAppLocale({
      userPref: claimLocale,
      browserPreferences:
        typeof navigator === "undefined" ? [] : Array.from(navigator.languages ?? []),
      appDefault: CUSTOMER_APP_DEFAULT_LOCALE,
    });
    // Sonucu önbelleğe yaz: bir sonraki boot bu zinciri claim gelmeden ÖNCE
    // koşturabilsin diye. Bu satır olmadan her yükleme İngilizce boyayıp
    // burada dil değiştirir — kullanıcının gördüğü "önce EN, sonra TR" budur.
    cacheResolvedLocale(resolved, localeStorage);
    if (resolved !== i18n.language) {
      void i18n.changeLanguage(resolved);
    }
  }, [claimLocale]);
}
